import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { createGroup } from "../../services/groupService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
}));

const GroupSchema = Yup.object().shape({
  whatsappId: Yup.number()
    .required(i18n.t("groupManagement.createModal.validation.connectionRequired")),
  name: Yup.string()
    .min(1, i18n.t("groupManagement.createModal.validation.nameLength"))
    .max(100, i18n.t("groupManagement.createModal.validation.nameLength"))
    .required(i18n.t("groupManagement.createModal.validation.nameRequired")),
  participants: Yup.array()
    .min(1, i18n.t("groupManagement.createModal.validation.participantsRequired"))
    .required(i18n.t("groupManagement.createModal.validation.participantsRequired")),
});

const CreateGroupModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();

  const [whatsapps, setWhatsapps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");

  useEffect(() => {
    if (open) {
      loadWhatsapps();
    }
  }, [open]);

  useEffect(() => {
    if (selectedWhatsapp) {
      loadContacts(selectedWhatsapp);
    }
  }, [selectedWhatsapp]);

  const loadWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(data);
    } catch (err) {
      toastError(err);
    }
  };

  const loadContacts = async (whatsappId) => {
    setLoadingContacts(true);
    try {
      const { data } = await api.get("/contacts", {
        params: {
          searchParam: "",
          pageNumber: 1,
        },
      });
      setContacts(data.contacts || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleClose = () => {
    setSelectedWhatsapp("");
    setContacts([]);
    onClose();
  };

  const handleSubmit = async (values, actions) => {
    try {
      // Extract phone numbers from selected contacts
      const participants = values.participants.map((contact) => contact.number);

      await createGroup(values.whatsappId, values.name, participants);

      toast.success(i18n.t("groupManagement.toasts.groupCreated"));
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const initialValues = {
    whatsappId: "",
    name: "",
    participants: [],
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("groupManagement.createModal.title")}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={GroupSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <div className={classes.root}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  error={touched.whatsappId && Boolean(errors.whatsappId)}
                >
                  <InputLabel>
                    {i18n.t("groupManagement.createModal.form.connection")}
                  </InputLabel>
                  <Select
                    label={i18n.t("groupManagement.createModal.form.connection")}
                    value={values.whatsappId}
                    onChange={(e) => {
                      setFieldValue("whatsappId", e.target.value);
                      setSelectedWhatsapp(e.target.value);
                      setFieldValue("participants", []); // Reset participants when connection changes
                    }}
                  >
                    {whatsapps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Field
                  as={TextField}
                  label={i18n.t("groupManagement.createModal.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  placeholder={i18n.t(
                    "groupManagement.createModal.form.namePlaceholder"
                  )}
                />

                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  error={touched.participants && Boolean(errors.participants)}
                >
                  <Autocomplete
                    multiple
                    options={contacts}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.number})`
                    }
                    value={values.participants}
                    onChange={(e, newValue) => {
                      setFieldValue("participants", newValue);
                    }}
                    loading={loadingContacts}
                    disabled={!selectedWhatsapp}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={i18n.t(
                          "groupManagement.createModal.form.participants"
                        )}
                        placeholder={
                          selectedWhatsapp
                            ? i18n.t(
                                "groupManagement.createModal.form.participantsPlaceholder"
                              )
                            : i18n.t(
                                "groupManagement.createModal.validation.connectionRequired"
                              )
                        }
                        error={touched.participants && Boolean(errors.participants)}
                        helperText={touched.participants && errors.participants}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingContacts ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                {i18n.t("groupManagement.createModal.buttons.cancel")}
              </Button>
              <div className={classes.btnWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {i18n.t("groupManagement.createModal.buttons.create")}
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateGroupModal;
